<template>
  <div class="ag-filter__wrapper">
    <div :id="id" class="ag-filter">
      <div class="ag-filter__content">
        <div v-if="$slots['prepend-title']" class="ag-filter__prepend-title">
          <slot name="prepend-title" />
        </div>

        <div class="ag-filter__title">
          <slot name="title" />
        </div>

        <div v-if="$slots['append-title'] || defaultNotEncapsulated" class="ag-filter__append-title">
          <slot name="append-title" />
          <slot v-if="!encapsulated" name="default" />
        </div>
      </div>

      <div class="ag-filter__actions">
        <AGButton
          v-if="!hideToggleButton && encapsulated"
          :id="`${id}-toggle-button`"
          icon-size="22px"
          left-icon="md:filter_alt"
          type="secondary"
          :disabled="disableToggle"
          @click="menu = !menu"
        >
          {{ filterToggleButtonLabel }}
        </AGButton>

        <AGButton v-if="!encapsulated" :id="`${id}-apply-button`" :disabled="disableApply" @click="emitClickApply">
          {{ applyButtonText }}
        </AGButton>

        <AGButton
          v-if="!encapsulated"
          :id="`${id}-clear-button`"
          icon-size="22px"
          left-icon="mdi-delete-outline"
          type="tertiary"
          :disabled="disableClear"
          @click="emitClickClear"
        >
          {{ clearButtonText }}
        </AGButton>

        <slot name="export">
          <template v-if="hasExportButton">
            <AGExportButton
              v-if="exportFormats.length === 1"
              :id="`${id}-export-button`"
              :disabled="disableExport"
              :loading="loadingExport"
              @click="emitClickExport"
            />

            <v-menu v-else>
              <template #activator="{ props, isActive }">
                <AGExportButton
                  v-bind="props"
                  :id="`${id}-export-button`"
                  :disabled="disableExport"
                  :loading="loadingExport"
                >
                  <template #append-inner>
                    <v-divider class="mr-2 ml-3" vertical />
                    <AGIcon :icon="isActive ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
                  </template>
                </AGExportButton>
              </template>

              <v-list :model-value="null">
                <v-list-item
                  v-for="format in exportFormats"
                  :key="format"
                  class="tile"
                  :value="format"
                  @click="emitClickExport(format)"
                >
                  {{ format }}
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </slot>

        <slot name="actions" />
      </div>
    </div>

    <AGFilterMenu
      v-model="menu"
      :target="`#${id}-toggle-button`"
      :disable-apply="disableApply"
      :disable-clear="disableClear"
      @click:apply="emitClickApply"
      @click:clear="emitClickClear"
      @click:close="emitClickClose"
    >
      <slot />
    </AGFilterMenu>
  </div>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGExportButton from '@/components/common/AGExportButton.vue';
import AGFilterMenu from '@/components/common/AGFilterMenu.vue';
import AGIcon from '@/components/common/AGIcon.vue';
import { OPCOES_EXPORTACAO } from '@/constants/ConstantOpcoesExportacao';
import { uniqueId } from 'lodash';

export default {
  components: {
    AGExportButton,
    AGIcon,
    AGButton,
    AGFilterMenu,
  },

  emits: ['click:apply', 'click:clear', 'click:close'],

  props: {
    id: {
      type: String,
      default: uniqueId('ag_filter'),
    },

    encapsulated: {
      type: Boolean,
      default: false,
    },

    hideTitle: {
      type: Boolean,
      default: false,
    },

    closeOnApply: {
      type: Boolean,
      default: false,
    },

    closeOnClear: {
      type: Boolean,
      default: false,
    },

    disableApply: {
      type: Boolean,
      default: false,
    },

    disableClear: {
      type: Boolean,
      default: false,
    },

    disableExport: {
      type: Boolean,
      default: false,
    },

    disableToggle: {
      type: Boolean,
      default: false,
    },

    toggleButtonText: {
      type: String,
      default: 'Filtro',
    },

    applyButtonText: {
      type: String,
      default: 'Aplicar filtro',
    },

    clearButtonText: {
      type: String,
      default: 'Limpar filtro',
    },

    exportButtonText: {
      type: String,
      default: 'Exportar',
    },

    filterQuantityText: {
      type: [String, Number],
      default: 0,
    },

    hideToggleButton: {
      type: Boolean,
      default: false,
    },

    loadingExport: {
      type: Boolean,
      default: false,
    },

    exportFormats: {
      type: [Array],
      default: [OPCOES_EXPORTACAO.CSV.getValue()],
    },
  },

  data: () => ({
    menu: false,
  }),

  computed: {
    filterToggleButtonLabel() {
      let label = this.toggleButtonText;

      if (this.filterQuantityText) label += ` (${this.filterQuantityText})`;

      return label;
    },

    defaultNotEncapsulated() {
      return this.$slots.default && !this.encapsulated;
    },

    hasExportButton() {
      return !!this.$attrs['onClick:export'];
    },
  },

  methods: {
    emitClickApply() {
      this.$emit('click:apply');
    },

    emitClickClear() {
      this.$emit('click:clear');
    },

    emitClickClose() {
      this.$emit('click:close');
    },

    emitClickExport(exportFormat = this.exportFormats[0]) {
      this.$emit('click:export', exportFormat);
    },

    fecharDropdown() {
      this.menu = false;
    },
  },
};
</script>

<style scoped lang="scss">
.ag-filter__wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
}

.ag-filter {
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;

  .ag-filter__content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    .ag-filter__prepend-title {
      flex: 1 1 auto;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }

    .ag-filter__title {
      flex: 1 0 auto;

      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }

    .ag-filter__append-title {
      flex: 1 0 auto;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 16px;
    }
  }

  .ag-filter__actions {
    flex: 0 1 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  }
}
</style>
